/**
 * Draft/applied filter selection for the regulation filter panel,
 * and the mapping onto the existing list search and global-search APIs.
 */

import dayjs, { type Dayjs } from "dayjs";
import type { ConceptValue } from "../../../services/regulationsService";
import {
  FILTER_DIMENSION_KEYS,
  type FilterDimension,
  type FilterOption,
} from "../../../services/regulationFilterDimensionsService";

export type FavoriteFilterValue = "favorited" | "not_favorited";

export interface FilterSelectionState {
  selections: Record<string, FilterOption[]>;
  customFrom: string;
  customTo: string;
}

export const PUBLISHED_DATE_PRESETS = [
  "today",
  "last_7_days",
  "last_30_days",
  "last_90_days",
  "last_180_days",
] as const;

export type PublishedDatePreset = (typeof PUBLISHED_DATE_PRESETS)[number];

export function emptyFilterSelection(): FilterSelectionState {
  return { selections: {}, customFrom: "", customTo: "" };
}

export function expandPublishedDatePreset(
  preset: string,
  today: Dayjs = dayjs(),
): { from: string; to: string } | null {
  const end = today.format("YYYY-MM-DD");
  const days: Record<string, number> = {
    today: 0,
    last_7_days: 7,
    last_30_days: 30,
    last_90_days: 90,
    last_180_days: 180,
  };
  const offset = days[preset];
  if (offset == null) return null;
  return {
    from: today.subtract(offset, "day").format("YYYY-MM-DD"),
    to: end,
  };
}

function ymd(value: Dayjs | null): string {
  return value && value.isValid() ? value.format("YYYY-MM-DD") : "";
}

export function matchPublishedDatePreset(
  from: Dayjs | null,
  to: Dayjs | null,
  today: Dayjs = dayjs(),
): PublishedDatePreset | null {
  const fromText = ymd(from);
  const toText = ymd(to);
  if (!fromText || !toText) return null;
  for (const preset of PUBLISHED_DATE_PRESETS) {
    const range = expandPublishedDatePreset(preset, today);
    if (range && range.from === fromText && range.to === toText) return preset;
  }
  return null;
}

export function isFilterOptionSelected(
  option: FilterOption,
  selected: FilterOption[],
): boolean {
  return selected.some(
    (item) =>
      item.value === option.value ||
      item.name === option.name ||
      (item.code != null && option.code != null && item.code === option.code),
  );
}

export function toggleFilterOption(
  selected: FilterOption[],
  option: FilterOption,
  selection: "single" | "multi",
): FilterOption[] {
  const already = isFilterOptionSelected(option, selected);
  if (selection === "single") {
    return already ? [] : [option];
  }
  if (already) {
    return selected.filter(
      (item) => !isFilterOptionSelected(option, [item]),
    );
  }
  return [...selected, option];
}

function publishedSelectionCounts(
  options: FilterOption[],
  customFrom: string,
  customTo: string,
): number {
  const preset = options[0]?.value;
  if (!preset) return 0;
  if (preset === "custom") {
    return customFrom || customTo ? 1 : 0;
  }
  return 1;
}

export function dimensionSelectionCount(
  key: string,
  state: FilterSelectionState,
): number {
  const options = state.selections[key] ?? [];
  if (key === FILTER_DIMENSION_KEYS.publishedDate) {
    return publishedSelectionCounts(options, state.customFrom, state.customTo);
  }
  return options.length;
}

export function countFilterSelections(state: FilterSelectionState): number {
  return Object.keys(state.selections).reduce(
    (total, key) => total + dimensionSelectionCount(key, state),
    0,
  );
}

export function orderFilterDimensions(
  dimensions: FilterDimension[],
  query: string,
  optionsByKey: Record<string, FilterOption[]> = {},
): FilterDimension[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return dimensions;
  return dimensions.filter((dimension) => {
    if (dimension.displayName.toLowerCase().includes(needle)) return true;
    return (optionsByKey[dimension.key] ?? []).some((option) =>
      option.name.toLowerCase().includes(needle),
    );
  });
}

export function visibleValueOptions(
  dimension: FilterDimension,
  options: FilterOption[],
  query: string,
): FilterOption[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return options;
  const matches = options.filter((option) =>
    option.name.toLowerCase().includes(needle),
  );
  if (
    matches.length === 0 &&
    dimension.displayName.toLowerCase().includes(needle)
  ) {
    return options;
  }
  return matches;
}

function optionIdentity(option: FilterOption): string {
  return option.code || option.value || option.name;
}

export function filterSelectionsEqual(
  left: FilterSelectionState,
  right: FilterSelectionState,
): boolean {
  if (left.customFrom !== right.customFrom || left.customTo !== right.customTo) {
    return false;
  }
  const keys = new Set([
    ...Object.keys(left.selections),
    ...Object.keys(right.selections),
  ]);
  for (const key of keys) {
    const a = (left.selections[key] ?? []).map(optionIdentity).sort();
    const b = (right.selections[key] ?? []).map(optionIdentity).sort();
    if (a.length !== b.length || a.some((value, index) => value !== b[index])) {
      return false;
    }
  }
  return true;
}

/**
 * Single-select keeps catalog order. Multi-select pins selected values to the
 * top, A–Z by name. Tags keep the order the user selected them.
 * Missing selected values can be included so they stay visible above the page.
 */
/** Append page results without repeating values already in the list. */
export function mergeFilterOptions(
  existing: FilterOption[],
  incoming: FilterOption[],
): FilterOption[] {
  const next = [...existing];
  for (const option of incoming) {
    if (!isFilterOptionSelected(option, next)) next.push(option);
  }
  return next;
}

export function pinSelectedOptions(
  options: FilterOption[],
  selected: FilterOption[],
  query: string,
  mode: {
    selection?: "single" | "multi";
    preserveSelectionOrder?: boolean;
    includeMissing?: boolean;
  } = {},
): FilterOption[] {
  if ((mode.selection ?? "multi") === "single") return options;

  const needle = query.trim().toLowerCase();
  const uniqueOptions = mergeFilterOptions([], options);
  const inOptions = (option: FilterOption) =>
    uniqueOptions.some((item) => isFilterOptionSelected(option, [item]));
  const unselected = uniqueOptions.filter(
    (option) => !isFilterOptionSelected(option, selected),
  );
  let pinned = selected.filter((option) => {
    if (inOptions(option)) return true;
    if (!mode.includeMissing) return false;
    return !needle || option.name.toLowerCase().includes(needle);
  });
  if (!mode.preserveSelectionOrder) {
    pinned = [...pinned].sort((left, right) =>
      left.name.localeCompare(right.name, undefined, { sensitivity: "base" }),
    );
  }
  const resolve = (option: FilterOption) =>
    uniqueOptions.find((item) => isFilterOptionSelected(option, [item])) ??
    option;
  return [...pinned.map(resolve), ...unselected];
}

function toConceptValue(option: FilterOption): ConceptValue {
  const code = option.code || option.value || option.name;
  return {
    id: option.id || code,
    code,
    name: option.name,
  };
}

export interface ListCriteriaFromFilter {
  markets: ConceptValue[];
  productTypes: ConceptValue[];
  healthAuthorities: ConceptValue[];
  categories: ConceptValue[];
  subCategories: ConceptValue[];
  topics: ConceptValue[];
  tags: ConceptValue[];
  fromDate: Dayjs | null;
  toDate: Dayjs | null;
  favorite: FavoriteFilterValue | null;
}

export function mapFilterSelectionToListCriteria(
  state: FilterSelectionState,
  today: Dayjs = dayjs(),
): ListCriteriaFromFilter {
  const favoriteValue = state.selections[FILTER_DIMENSION_KEYS.favorite]?.[0]?.value;
  const favorite =
    favoriteValue === "favorited" || favoriteValue === "not_favorited"
      ? favoriteValue
      : null;
  const preset = state.selections[FILTER_DIMENSION_KEYS.publishedDate]?.[0]?.value;
  let fromDate: Dayjs | null = null;
  let toDate: Dayjs | null = null;
  if (preset && preset !== "custom") {
    const range = expandPublishedDatePreset(preset, today);
    if (range) {
      fromDate = dayjs(range.from);
      toDate = dayjs(range.to);
    }
  } else if (preset === "custom") {
    fromDate = state.customFrom ? dayjs(state.customFrom) : null;
    toDate = state.customTo ? dayjs(state.customTo) : null;
  }

  const concepts = (key: string) =>
    (state.selections[key] ?? []).map(toConceptValue);

  return {
    markets: concepts(FILTER_DIMENSION_KEYS.markets),
    productTypes: concepts(FILTER_DIMENSION_KEYS.productType),
    healthAuthorities: concepts(FILTER_DIMENSION_KEYS.healthAuthority),
    categories: concepts(FILTER_DIMENSION_KEYS.category),
    subCategories: concepts(FILTER_DIMENSION_KEYS.subCategory),
    topics: concepts(FILTER_DIMENSION_KEYS.topics),
    tags: concepts("tags"),
    fromDate,
    toDate,
    favorite,
  };
}

function conceptToOption(
  item: ConceptValue,
  valueFrom: "code" | "name",
): FilterOption {
  const code = item.code || item.name;
  return {
    value: valueFrom === "name" ? item.name : code,
    name: item.name,
    code,
    id: item.id,
  };
}

export function listCriteriaToFilterSelection(input: {
  markets?: ConceptValue[];
  productTypes: ConceptValue[];
  healthAuthorities: ConceptValue[];
  categories?: ConceptValue[];
  subCategories?: ConceptValue[];
  topics?: ConceptValue[];
  tags: ConceptValue[];
  fromDate: Dayjs | null;
  toDate: Dayjs | null;
  favorite: FavoriteFilterValue | null;
  today?: Dayjs;
}): FilterSelectionState {
  const today = input.today ?? dayjs();
  const selections: Record<string, FilterOption[]> = {};
  const assign = (
    key: string,
    items: ConceptValue[] | undefined,
    valueFrom: "code" | "name",
  ) => {
    if (!items || items.length === 0) return;
    selections[key] = items.map((item) => conceptToOption(item, valueFrom));
  };
  assign(FILTER_DIMENSION_KEYS.markets, input.markets, "name");
  assign(FILTER_DIMENSION_KEYS.productType, input.productTypes, "code");
  assign(FILTER_DIMENSION_KEYS.healthAuthority, input.healthAuthorities, "name");
  assign(FILTER_DIMENSION_KEYS.category, input.categories, "name");
  assign(FILTER_DIMENSION_KEYS.subCategory, input.subCategories, "name");
  assign(FILTER_DIMENSION_KEYS.topics, input.topics, "name");
  assign("tags", input.tags, "name");
  if (input.favorite) {
    selections[FILTER_DIMENSION_KEYS.favorite] = [
      {
        value: input.favorite,
        name: input.favorite === "favorited" ? "Favorited" : "Not favorited",
      },
    ];
  }

  let customFrom = "";
  let customTo = "";
  const preset = matchPublishedDatePreset(input.fromDate, input.toDate, today);
  if (preset) {
    selections[FILTER_DIMENSION_KEYS.publishedDate] = [
      {
        value: preset,
        name: preset,
      },
    ];
  } else if (input.fromDate || input.toDate) {
    selections[FILTER_DIMENSION_KEYS.publishedDate] = [
      { value: "custom", name: "Custom range" },
    ];
    customFrom = ymd(input.fromDate);
    customTo = ymd(input.toDate);
  }

  return { selections, customFrom, customTo };
}

/**
 * Global search only accepts `includes` on the existing POST /global-search body.
 * Favorite and published-date presets are not part of that contract.
 */
export function filterSelectionToIncludes(state: FilterSelectionState): string[] {
  const skip = new Set<string>([
    FILTER_DIMENSION_KEYS.favorite,
    FILTER_DIMENSION_KEYS.publishedDate,
  ]);
  const names: string[] = [];
  for (const [key, options] of Object.entries(state.selections)) {
    if (skip.has(key)) continue;
    for (const option of options) {
      if (option.name.trim()) names.push(option.name);
    }
  }
  return names;
}
